import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { useFormik } from 'formik';
import { useSkillsListQuery, useUpdateUserDetailsMutation } from 'services';
import { skillSchema } from 'constants/schemaValidations';
import useTranslate from 'hooks/useTranslate';
import useUserStore from 'store/user';
import toast from 'react-hot-toast';
import { SkillInterface } from 'constants/interfaces';
import useCommonStore from 'store/common';

const AddSkills: React.FC<any> = () => {
	const { data: { skill = [] } = {} } = useSkillsListQuery();
	const { modalConfig = {} }: any = useCommonStore((state) => state);
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();
	const { translate } = useTranslate();
	const { profileData }: any = useUserStore((state) => state);
	const { skills: savedData = [] } = profileData;
	const formik: any = useFormik({
		initialValues: {
			skills: savedData?.map((item: any) => item.id) ?? [],
		},
		validationSchema: skillSchema(translate),
		enableReinitialize: true,
		onSubmit: async (values) => {
			updateProfile({ ...values, saveType: 'skills' })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => modalConfig?.onClose());
		},
	});
	const getNameById = (arr: any, id: any, key: any) => {
		if (arr?.length) {
			return arr.find((item: any) => item?.id === Number(id))?.[key];
		}
	};

	const renderError = <T extends keyof SkillInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.addSkill')}</h4>
					<Row>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Common.skill')}</Form.Label>
								<ReactSelect
									isSearchable
									name="skills"
									options={skill}
									getOptionValue={(option) => option.id}
									getOptionLabel={(option) => option.title}
									onChange={(optionValue: any) => {
										const valuesArr = [];
										if (formik.values.skills?.length) {
											valuesArr.push(...formik.values.skills, optionValue?.id?.toString());
										} else {
											valuesArr.push(optionValue?.id?.toString());
										}
										formik.setFieldValue('skills', valuesArr);
									}}
									onBlur={formik.handleBlur}
									autoFocus={false}
									placeholder={translate('Profile.addSkill')}
									className="form-react-select"
									classNamePrefix="select"
									isOptionDisabled={(option: any) =>
										formik.values.skills?.some((item: any) => Number(item) === option?.id)
									}
								/>
								{renderError('skills')}
							</Form.Group>

							{formik.values.skills?.length > 0 && (
								<div className="selected-items-list d-flex flex-wrap gap-2 gap-md-3 mt-3 mt-md-4">
									{formik.values.skills.map((skillData: any) => (
										<div
											key={skillData}
											className="selected-item-box d-flex align-items-center gap-2 rounded-pill"
										>
											<p className="mb-0">{getNameById(skill, skillData, 'title')}</p>
											<Button
												variant="link"
												className="p-0 text-decoration-none border-0"
												onClick={() => {
													const updatedSkills = formik.values.skills.filter(
														(skillValue: any) => skillValue !== skillData,
													);
													formik.setFieldValue('skills', updatedSkills);
												}}
											>
												<i className="ri-close-line"></i>
											</Button>
										</div>
									))}
								</div>
							)}
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit">
							{translate('Common.save')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default AddSkills;
